import React from "react";
import styled, { css } from "styled-components";
import PropTypes from "prop-types";
import Image from "next/image";
import dyanmicShortCodeText from "components/Contentful/utils";

const OuterWrapper = styled.section`
  max-width: 82rem;
  margin: 3.5625rem 1.5625rem;
`;

const Header = styled.div`
  font-size: ${({ theme }) => theme.fontSize(5)};
  font-weight: 600;
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  text-align: center;
  line-height: 1.2;

  ${({ theme }) => theme.mq.md`
    margin-bottom: 3.875rem;
    font-size: ${theme.fontSize(6)};
  `};
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: 1fr;

  ${({ theme }) => theme.mq.md`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    grid-gap: 3rem;
    ${({ lessThanFour }) =>
      lessThanFour &&
      css`
        grid-template-columns: 1fr;
        grid-template-rows: auto;
      `};
  `};
  ${({ theme }) => theme.mq.lg`
    display: flex;
    justify-content: space-between;
    flex-direction: row;
  `};
`;

const Item = styled.div`
  display: grid;
  grid-template-rows: auto 0.5fr 1fr 0.5fr;
  justify-items: center;
  margin: 1.875rem 0 0;
  row-gap: 0.5rem;
  ${({ theme }) => theme.mq.md`
    margin: 0
  `};
`;

const Title = styled(Header)`
  font-size: ${({ theme }) => theme.fontSize(4)};
  margin-bottom: 0;
`;

const Subtitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize(2)};
  font-weight: 400;
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  text-align: center;
  word-wrap: break-word;
  max-width: 16.6875rem;
`;

const AccessoryText = styled.div`
  font-size: ${({ theme }) => theme.fontSize(1)};
  font-weight: 600;
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  justify-self: center;
`;

const ImageWrapper = styled.div`
  margin-bottom: 0.5rem;
  display: flex;
  justify-items: center;
  flex: 1 0 auto;
`;

const StyledImage = styled(Image)`
  width: 100%;
`;

const GraphicListBlock = ({ blockData, cityName }) => {
  const listItemLength = blockData?.listItems?.length || 0;
  const isLessThanFour = listItemLength < 4;

  const contentfulVarDict = {
    city: cityName,
  };

  const title = dyanmicShortCodeText({
    textToSearch: blockData?.title,
    replaceDictLookup: contentfulVarDict,
  });
  return (
    <OuterWrapper>
      <Header>{title}</Header>
      <ContentWrapper lessThanFour={isLessThanFour}>
        {blockData?.listItems?.map((item) => {
          const fields = item?.fields;
          const itemTitle = dyanmicShortCodeText({
            textToSearch: fields?.title,
            replaceDictLookup: contentfulVarDict,
          });
          const itemSubtitle = dyanmicShortCodeText({
            textToSearch: fields?.subtitle,
            replaceDictLookup: contentfulVarDict,
          });
          const itemAccessoryText = dyanmicShortCodeText({
            textToSearch: fields?.accessoryText,
            replaceDictLookup: contentfulVarDict,
          });

          return (
            <Item key={item?.sys?.id}>
              <ImageWrapper>
                <StyledImage
                  alt={fields?.icon?.fields?.file?.fileName}
                  layout="intrinsic"
                  height={fields?.icon?.fields?.file?.details?.image?.height}
                  width={fields?.icon?.fields?.file?.details?.image?.width}
                  src={`https:${fields?.icon?.fields?.file?.url}`}
                />
              </ImageWrapper>
              <Title>{itemTitle}</Title>
              <Subtitle>{itemSubtitle}</Subtitle>
              {fields?.accessoryText && (
                <AccessoryText>{itemAccessoryText}</AccessoryText>
              )}
            </Item>
          );
        })}
      </ContentWrapper>
    </OuterWrapper>
  );
};

export default GraphicListBlock;

GraphicListBlock.propTypes = {
  blockData: PropTypes.shape({
    title: PropTypes.string,
    listItems: PropTypes.arrayOf(
      PropTypes.shape({
        sys: PropTypes.shape({
          id: PropTypes.string,
        }),
        fields: PropTypes.shape({
          accessoryText: PropTypes.string,
          internalName: PropTypes.string,
          subtitle: PropTypes.string,
          title: PropTypes.string,
          icon: PropTypes.shape({
            fields: PropTypes.shape({
              title: PropTypes.string,
              file: PropTypes.shape({
                fileName: PropTypes.string,
                contentType: PropTypes.string,
                details: PropTypes.shape({
                  image: PropTypes.shape({
                    height: PropTypes.number,
                    width: PropTypes.number,
                  }),
                }),
              }),
            }),
          }),
        }),
      })
    ),
  }),
  cityName: PropTypes.string,
};

GraphicListBlock.defaultProps = {
  blockData: undefined,
  cityName: "",
};
